import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { Drawer, Input } from "antd"
import { debounce } from "lodash"
import dayjs from "moment"
import { useEffect, useMemo, useState } from "react"
import { FAILED_CATEGORY_CONFIG, TIMESHEET_DRAWER_WIDTH } from "../constants"
import type { UploadTimeSheetFailedEntity } from "../types"
import { countAffected } from "../utils"

interface Props {
  open: boolean
  failed: UploadTimeSheetFailedEntity | undefined
  failedType: string | null
  onClose: () => void
}

interface AffectedRowData {
  employeeId: number | string
  date: string
  clockinTime: string
  clockoutTime: string
  shiftPeriodId: number | string
  clockinLatitude: string
}

const buildRows = (
  failed: UploadTimeSheetFailedEntity | undefined
): AffectedRowData[] => {
  if (!failed) return []
  return failed.employeesAffected.flatMap((group) =>
    (group.employees ?? []).map((emp) => {
      const rec = emp.record
      return {
        employeeId: emp.employeeId ?? rec?.employeeId ?? "",
        date: rec?.date ?? "",
        clockinTime: rec?.clockin_time ?? "",
        clockoutTime: rec?.clockout_time ?? "",
        shiftPeriodId: rec?.shift_period_id ?? "—",
        clockinLatitude: rec?.clockin_latitude ?? "",
      }
    })
  )
}

const filterRows = (
  rows: AffectedRowData[],
  search: string
): AffectedRowData[] => {
  const q = search.trim().toLowerCase()
  if (!q) return rows
  return rows.filter((r) => {
    return (
      String(r.employeeId).toLowerCase().includes(q) ||
      r.date.toLowerCase().includes(q) ||
      r.clockinTime.toLowerCase().includes(q) ||
      r.clockoutTime.toLowerCase().includes(q) ||
      String(r.shiftPeriodId).toLowerCase().includes(q) ||
      r.clockinLatitude.toLowerCase().includes(q)
    )
  })
}

const cellBase =
  "pl-4 align-middle text-sm text-[#020618] border-b border-[#E2E8F0]"

const AffectedEmployeesDrawer = ({
  open,
  failed,
  failedType,
  onClose,
}: Props) => {
  const [search, setSearch] = useState("")
  const [searchedWord, setSearchedWord] = useState("")
  const debouncedSearch = useMemo(
    () => debounce((value: string) => setSearchedWord(value), 250),
    []
  )

  useEffect(() => {
    debouncedSearch(search)
    return () => debouncedSearch.cancel()
  }, [search, debouncedSearch])

  const config = failedType ? FAILED_CATEGORY_CONFIG[failedType] : undefined
  const rows = useMemo(() => buildRows(failed), [failed])
  const filtered = useMemo(
    () => filterRows(rows, searchedWord),
    [rows, searchedWord]
  )
  const count = failed ? countAffected(failed) : 0

  const handleClose = () => {
    setSearch("")
    setSearchedWord("")
    onClose()
  }

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={null}
      closable={false}
      width={TIMESHEET_DRAWER_WIDTH}
      footer={null}
      styles={{ body: { padding: 0 } }}
    >
      <div className="h-full flex flex-col font-circular">
        {/* Header */}
        <div className="flex items-start justify-between px-4 pt-4 pb-4">
          <div className="flex flex-col gap-1">
            <h3 className="font-bold text-lg text-[#0F172B] m-0">
              {config?.title ?? "Affected records"}
            </h3>
            <p className="text-sm text-[#62748E] m-0">
              {config
                ? config.description(count)
                : "Records in your timesheet that need attention."}
            </p>
          </div>
          <button
            type="button"
            className="p-1 hover:bg-[#F1F5F9] rounded text-[#62748E] text-xl leading-none"
            onClick={handleClose}
          >
            ✕
          </button>
        </div>

        {/* Search */}
        <div className="px-4 border-b border-[#E2E8F0]">
          <Input
            placeholder="Search"
            prefix={<MagnifyingGlassIcon className="w-4 h-4 text-[#94A3B8]" />}
            className="!border-0 !shadow-none !outline-none h-12"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto overflow-x-auto px-4">
          <table className="table-fixed border-collapse border-spacing-0">
            <colgroup>
              <col style={{ width: 200 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 200 }} />
            </colgroup>
            <thead>
              <tr className="bg-[#F8FAFC]">
                <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
                  Employee ID
                </th>
                <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
                  Date
                </th>
                <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
                  Clock-in
                </th>
                <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
                  Clock-out
                </th>
                <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
                  Shift period ID
                </th>
                <th className="text-left text-xs font-normal text-[#62748E] pl-4 py-3 border-b border-[#E2E8F0]">
                  Clock-in latitude
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-sm text-[#62748E]"
                  >
                    No records to display.
                  </td>
                </tr>
              ) : (
                filtered.map((r, idx) => (
                  <tr
                    key={`${r.employeeId}-${r.date}-${idx}`}
                    className="h-[100px]"
                  >
                    <td className={cellBase}>{r.employeeId || "—"}</td>
                    <td className={cellBase}>
                      {r.date ? dayjs(r.date).format("MMM DD YYYY") : "—"}
                    </td>
                    <td className={cellBase}>{r.clockinTime || "-"}</td>
                    <td className={cellBase}>{r.clockoutTime || "-"}</td>
                    <td className={cellBase}>{r.shiftPeriodId}</td>
                    <td className={cellBase}>{r.clockinLatitude || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Drawer>
  )
}

export default AffectedEmployeesDrawer
